import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import { useLocation } from "react-router-dom";
import { AppDispatch } from "src/shared/state/store";
import {VOBFormData, NetworkType, MemberType} from "../types";
import {Client, Facility, LevelOfCare, Payor, Plan, VobClassification} from '@finpay-estimation/estimation-types';
import { Vob } from '@finpay-estimation/estimation-types';
import { admissionsAdvisorService } from "@finpay-development/shared-platform/lib/esm/admissions-advisor/services/admissions-advisor-services";
import {getPtRecord} from "@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk";
import {crmInboundErrorMessages, AvailityVOBAErrorMessages} from "@finpay-development/shared-platform/lib/esm/shared/enums";
import {showErrorStatus} from "@finpay-development/shared-platform/lib/esm/security/state/user-slice";
import {RootState} from "../../../../../shared/state/root-reducer";
import {ClientFacility} from "@finpay-development/shared-platform/lib/esm/shared/model/client-status-card";
import {getEstimatorFacilityPayers} from "@finpay-development/shared-platform/lib/esm/admissions-advisor/state/vob-thunk";
import {
    createSelectedVob,
    saveSelectedVob,
} from '@finpay-development/shared-platform/lib/esm/shared/state/vob-thunk';
import {setSelectedVob} from "@finpay-development/shared-platform/lib/esm/shared/state/vob-slice";
import {loadPatientVobList} from "@finpay-development/shared-platform/lib/esm/shared/state/vob-thunk";
import moment from 'moment';
import {convertCRMVOBToNewVob} from "../utils/vob-converter";
import {resolveDefaultFacilityId} from "../utils/vob-utils";
import { PayorPlan } from '@finpay-estimation/estimation-types';
import {clearSelectedEstimate, estimatorSlice} from "@finpay-development/shared-platform/lib/esm/shared/state/estimator-slice";
import { loadAndSetRateAsPayorPlan } from "../../../../../estimates/utils/payor-plan-utils";
import { isActivePolicyEffectiveToBeforeToday } from "../utils/active-policy-effective-to-validation";
import {
    buildCrmVobUpdatePayload,
    checkIntegrationFeatureFlag,
    getClientCrmDetails,
    integrationVOBUpdate,
} from "../utils/crm-vob-sync-utils";
import { getAvailityVobWithOverrides } from "../../../patient/patient-form/utils/availity-vob";
export function useVOBForm(
    onTabChange?: () => void,
    onSetButtonStates?: (buttonStates: {
        isDeductibleButtonPressed: boolean;
        isOopmButtonPressed: boolean;
        isServiceLimitsButtonPressed: boolean;
        isMaxDaysButtonPressed: boolean;
        isCoinsuranceButtonPressed: boolean;
    }) => void,
    /** Called when building VOB for save; return value is written to vob.rateAsPayorPlan (does not update Redux) */
    getRateAsPayorPlanToSave?: (facilityPayersState: any, payersMap: Map<number, any>, rateAsPayorPlan: PayorPlan | null) => PayorPlan | null | undefined
) {
    const vobs = useSelector((state: RootState) => state.vobContext.patientVobList || []);
    const location = useLocation();

    const { patientId: urlPatientId, vobId: urlVobId, estimateId: urlEstimateId } = useParams<{
        patientId: string;
        vobId: string;
        estimateId: string;
    }>();

    const allClientsMap = useSelector((state: RootState) => state.implementationContext?.implementationSpecialistClient.allClientsMap);
    const allClientVobs = useSelector((state: RootState) => state.implementationContext?.implementationSpecialistClient.allClientVobs);

    const payers = useSelector(
        (state: RootState) =>
            state.adminContext.adminConfigurationContext?.payers
    );
    const payersMap = useSelector(
        (state: RootState) =>
            state.adminContext.adminConfigurationContext?.payersMap
    );
    const facilityPayersState = useSelector(
        (state: RootState) =>
            state.admissionsAdvisorContext.vobContext?.facilityPayers
    );

    const vobClassifications = useSelector(
        (state: RootState) =>
            state.vobContext.vobClassifications || []
    );
    const rateAsPayorPlan = useSelector(
        (state: RootState) => state.estimatorContext.rateAsPayorPlan as PayorPlan
    );
    const vobSources = useSelector((state: RootState) => state.metaData.vobSource) || [];

    const patientId = parseInt(urlPatientId || "");
    const estimateId = parseInt(urlEstimateId || "");
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const selectedEncounter = useSelector((state: RootState) => state.patientContext.selectedEncounter);
    const selectedEstimate = useSelector(
        (state: RootState) => state.estimatorContext.selectedEstimate
    );
    const allClients = useSelector(
        (state: RootState) =>
            state.implementationContext?.implementationSpecialistClient
                .allClientsWithFacillities
    );
    const selectedPatient = useSelector(
        (state: RootState) => state.patientContext.selectedPatient
    );
    const selectedPatientCurrentInsurance = useSelector(
        (state: RootState) => state.patientContext.selectedPatientCurrentInsurance
    );
    const estimateVobId = useSelector(
        (state: RootState) => state.estimatorContext.selectedEstimate?.vobId
    );
    const levelsOfCare = useSelector(
        (state: RootState) => state.estimatorContext.facilityLevelsOfCareList
    );
    const reduxRateAsPayorMap = useSelector((state: RootState) => state.estimatorContext.rateAsPayorMap) || {};
    const reduxMasterPayors = useSelector((state: RootState) => state.adminContext.adminConfigurationContext?.payersMap) || new Map();
    const [activePolicy, setActivePolicy] = useState(true);
    const [carryover, setCarryover] = useState(false);
    const [selectedVOB, setSelectedVOB] = useState(urlVobId || '');
    const [facilities, setFacilities] = useState<ClientFacility[]>([]);
    const [selectedFacility, setSelectedFacility] = useState<number | ''>('');
    const [networkType, setNetworkType] = useState<NetworkType>('in');
    const [memberType, setMemberType] = useState<MemberType>('individual');
    const [liveVOB, setLiveVOB] = useState(false);
    const currentYear = new Date().getFullYear();
    const [effectiveFromDate, setEffectiveFromDate] = useState<Date | null>(moment(`${currentYear}-01-01`).toDate());
    const [effectiveToDate, setEffectiveToDate] = useState<Date | null>(moment(`${currentYear}-12-31`).toDate());
    const [levelsLoading, setLevelsLoading] = useState(false);
    const [levelsError, setLevelsError] = useState<string | null>(null);
    const [isRunningCRMVOB, setIsRunningCRMVOB] = useState(false);
    const [isRunningAvailityVOB, setIsRunningAvailityVOB] = useState(false);
    const [facilitiesLoading, setFacilitiesLoading] = useState(false);
    const [isLoadingRunAvailityVOBButton, setIsLoadingRunAvailityVOBButton] = useState(false);
    const [showAvailityErrorDialog, setShowAvailityErrorDialog] = useState(false);
    const [availityErrorMessage, setAvailityErrorMessage] = useState<string | null>(null);
    const autoFixActivePolicyAttemptedForUrlVobIdRef = useRef<string | null>(null);
    const [formData, setFormData] = useState<VOBFormData>({
        deductible: '',
        remaining: '',
        oopm: '',
        oopmRemaining: '',
        copay: '',
        maxDays: '',
        coinsurance: '',
        notes: '',
        isDedIncOOPM: true,
    });

    const [isPayerMatched, setIsPayerMatched] = useState<boolean>(false);
    const [comparisonError, setComparisonError] = useState<string | null>(null);
    const currentVob = useSelector((state: RootState) => state.vobContext.selectedVob);
    const [savedFormDataMap, setSavedFormDataMap] = useState<Map<string, any>>(new Map());
    const [savedLOCDataMap, setSavedLOCDataMap] = useState<Map<number, any>>(new Map());
    const API_GET_FILTERED = -2;

    const isVOBUnique = (convertedVob: any) => {
        return !vobs.some(existingVob =>
            existingVob.facility?.facilityId === convertedVob.facility?.facilityId &&
            existingVob.payor?.payorName === convertedVob.payor?.payorName &&
            existingVob.plan?.planName === convertedVob.plan?.planName
        );
    };

    const isVobBusinessDuplicate = (newVob: Vob): boolean => {
        const getDate = (dt?: string) => dt ? dt.split('T')[0] : '';
        const today = new Date().toISOString().split('T')[0];

        return vobs.some(existingVob => {
            return (
                existingVob.vobSourceId === newVob.vobSourceId &&
                existingVob.facility?.facilityId === newVob.facility?.facilityId &&
                existingVob.payor?.payorId === newVob.payor?.payorId &&
                existingVob.plan?.payorPlanId === newVob.plan?.payorPlanId &&
                getDate(existingVob.createDt) === today
            );
        });
    };

    useEffect(() => {
        if (!selectedPatient.clientId || selectedFacility === '' || !allClientVobs) return;

        // First find all matching allClientVobs based on selectedPatient.clientId
        const clientVobsForPatient = allClientVobs.filter(clientVob =>
            clientVob.clientId === Number(selectedPatient.clientId)
        );

        // Check if selectedFacility exists in clientVobsForPatient
        const facilityExistsInClientVobs = clientVobsForPatient.some(clientVob =>
            clientVob.clientFacilityId === (selectedFacility as number)
        );

        // If facility exists, check if there are VOB records that meet the conditions
        let hasValidVobConfig = false;
        if (facilityExistsInClientVobs) {
            const validVobConfig = clientVobsForPatient.find(clientVob =>
                clientVob.clientFacilityId === (selectedFacility as number) &&
                clientVob.vobTypeId === 3 &&
                clientVob.isIntEnabled === true &&
                (clientVob as { isVerifyEnabled?: boolean }).isVerifyEnabled !== false
            );
            hasValidVobConfig = !!validVobConfig;
        }

        // If facility doesn't exist, check if there are other VOB records that meet the conditions
        if (!facilityExistsInClientVobs) {
            const anyValidVobConfig = clientVobsForPatient.find(clientVob =>
                clientVob.vobTypeId === 3 &&
                clientVob.isIntEnabled === true &&
                (clientVob as { isVerifyEnabled?: boolean }).isVerifyEnabled !== false &&
                clientVob.clientFacilityId === null
            );
            const hasAnyValidConfig = !!anyValidVobConfig;

            if (hasAnyValidConfig) {
                setIsLoadingRunAvailityVOBButton(true);
            } else {
                setIsLoadingRunAvailityVOBButton(false);
            }
        } else {
            // If facility exists, use the previous hasValidVobConfig logic
            if (hasValidVobConfig) {
                setIsLoadingRunAvailityVOBButton(true);
            } else {
                setIsLoadingRunAvailityVOBButton(false);
            }
        }
    }, [selectedPatient.clientId, selectedFacility, allClientVobs]);

    useEffect(() => {
        if (!showAvailityErrorDialog) {
            setAvailityErrorMessage(null);
        }
    }, [showAvailityErrorDialog]);

    useEffect(() => {
        if (patientId && (!vobs || vobs.length === 0)) {
            dispatch(loadPatientVobList(patientId));
        }
    }, [patientId, dispatch]);

    useEffect(() => {
        if (currentVob?.client?.clientId !== undefined) {
            const fetchFacilities = async () => {
                setFacilitiesLoading(true);
                try {
                    const clientId = Number(currentVob?.client?.clientId);
                    const targetClient = allClientsMap?.get(clientId);
                    if (targetClient?.clientFacilities) {
                        setFacilities(targetClient.clientFacilities);
                        if (targetClient.clientFacilities.length > 0) {
                            const facilityId = currentVob?.facility?.facilityId
                                ? Number(currentVob.facility.facilityId)
                                : targetClient.clientFacilities[0].facilityId;
                            setSelectedFacility(facilityId);
                        }
                    }
                } catch (err) {
                    console.error('Error fetching facilities:', err);
                } finally {
                    setFacilitiesLoading(false);
                }
            };
            fetchFacilities();
        } else if (!urlVobId && selectedPatient?.clientId) {
            setFacilitiesLoading(true);
            try {
                const targetClient = allClientsMap?.get(Number(selectedPatient.clientId));
                if (targetClient?.clientFacilities) {
                    setFacilities(targetClient.clientFacilities);
                    const defaultFacilityId = resolveDefaultFacilityId(
                        targetClient.clientFacilities,
                        selectedEncounter?.facilityId
                    );
                    if (defaultFacilityId != null) {
                        setSelectedFacility(defaultFacilityId);
                    }
                }
            } catch (err) {
                console.error('Error fetching facilities:', err);
            } finally {
                setFacilitiesLoading(false);
            }
        }
    }, [currentVob, urlVobId, selectedPatient, allClientsMap, selectedEncounter?.facilityId]);

    useEffect(() => {
        if (!currentVob) return;
        const processClassifications = (classifications: VobClassification[] | undefined, networkType: 'in' | 'out') => {
            if (!classifications || classifications.length === 0) return;
            classifications.forEach(classification => {
                setSavedLOCDataMap(prevMap => {
                    const newMap = new Map(prevMap);
                    const existingLocDataMap = newMap.get(classification.vobClassificationId) || new Map();

                    const individualData = {
                        total: classification.deductible?.total?.toString() || '',
                        remaining: classification.deductible?.remaining?.toString() || '',
                        oopmTotal: classification.oopm?.total?.toString() || '',
                        oopmRemaining: classification.oopm?.remaining?.toString() || '',
                        deductibleIncluded: classification.oopm?.isDeductibleIncluded ?? true,
                        perDayVisit: classification.coPay?.toString() || '',
                        maxDays: classification.maxDays?.toString() || '',
                        patient: classification.coInsurance?.toString() || '',
                    };

                    const familyData = {
                        total: classification.familyDeductible?.total?.toString() || '',
                        remaining: classification.familyDeductible?.remaining?.toString() || '',
                        oopmTotal: classification.familyOopm?.total?.toString() || '',
                        oopmRemaining: classification.familyOopm?.remaining?.toString() || '',
                        deductibleIncluded: classification.familyOopm?.isDeductibleIncluded ?? true,
                        perDayVisit: classification.familyCoPay?.toString() || '',
                        maxDays: classification.familyMaxDays?.toString() || '',
                        patient: classification.familyCoInsurance?.toString() || '',
                    };

                    existingLocDataMap.set(`${networkType}_individual`, individualData);
                    existingLocDataMap.set(`${networkType}_family`, familyData);

                    const notesText = (classification.notes ?? [])
                        .map(n => (n?.noteText ?? '').trim())
                        .filter(Boolean)
                        .join('\n');
                    existingLocDataMap.set(`${networkType}_notes`, notesText);

                    newMap.set(classification.vobClassificationId, existingLocDataMap);
                    return newMap;
                });
            });
        };
        processClassifications(currentVob.innClassifications, 'in');
        processClassifications(currentVob.oonClassifications, 'out');

        const inIndividualData = {
            deductible: currentVob.innDeductible?.total?.toString() || '',
            remaining: currentVob.innDeductible?.remaining?.toString() || '',
            oopm: currentVob.innOopm?.total?.toString() || '',
            oopmRemaining: currentVob.innOopm?.remaining?.toString() || '',
            copay: currentVob.innCoPay?.toString() || '',
            maxDays: currentVob.innMaxDays?.toString() || '',
            coinsurance: currentVob.innCoInsurance?.toString() || '',
            isDedIncOOPM: currentVob.innOopm?.isDeductibleIncluded ?? true,
        };

        setSavedFormDataMap(prevMap => {
            const newMap = new Map(prevMap);
            newMap.set('in_individual', inIndividualData);
            return newMap;
        });


        const inFamilyData = {
            deductible: currentVob.innFamilyDeductible?.total?.toString() || '',
            remaining: currentVob.innFamilyDeductible?.remaining?.toString() || '',
            oopm: currentVob.innFamilyOopm?.total?.toString() || '',
            oopmRemaining: currentVob.innFamilyOopm?.remaining?.toString() || '',
            copay: currentVob.innFamilyCoPay?.toString() || '',
            maxDays: currentVob.innFamilyMaxDays?.toString() || '',
            coinsurance: currentVob.innFamilyCoInsurance?.toString() || '',
            isDedIncOOPM: currentVob.innFamilyOopm?.isDeductibleIncluded ?? true,
        };

        setSavedFormDataMap(prevMap => {
            const newMap = new Map(prevMap);
            newMap.set('in_family', inFamilyData);
            return newMap;
        });


        const oonIndividualData = {
            deductible: currentVob.oonDeductible?.total?.toString() || '',
            remaining: currentVob.oonDeductible?.remaining?.toString() || '',
            oopm: currentVob.oonOopm?.total?.toString() || '',
            oopmRemaining: currentVob.oonOopm?.remaining?.toString() || '',
            copay: currentVob.oonCoPay?.toString() || '',
            maxDays: currentVob.oonMaxDays?.toString() || '',
            coinsurance: currentVob.oonCoInsurance?.toString() || '',
            isDedIncOOPM: currentVob.oonOopm?.isDeductibleIncluded ?? true,
        };

        setSavedFormDataMap(prevMap => {
            const newMap = new Map(prevMap);
            newMap.set('out_individual', oonIndividualData);
            return newMap;
        });

        const oonFamilyData = {
            deductible: currentVob.oonFamilyDeductible?.total?.toString() || '',
            remaining: currentVob.oonFamilyDeductible?.remaining?.toString() || '',
            oopm: currentVob.oonFamilyOopm?.total?.toString() || '',
            oopmRemaining: currentVob.oonFamilyOopm?.remaining?.toString() || '',
            copay: currentVob.oonFamilyCoPay?.toString() || '',
            maxDays: currentVob.oonFamilyMaxDays?.toString() || '',
            coinsurance: currentVob.oonFamilyCoInsurance?.toString() || '',
            isDedIncOOPM: currentVob.oonFamilyOopm?.isDeductibleIncluded ?? true,
        };

        setSavedFormDataMap(prevMap => {
            const newMap = new Map(prevMap);
            newMap.set('out_family', oonFamilyData);
            return newMap;
        });

        updateFormDataFromVOB(currentVob);
        const currentYear = new Date().getFullYear();
        setEffectiveFromDate(currentVob.policyBeginDate ? moment(currentVob.policyBeginDate).toDate() : moment(`${currentYear}-01-01`).toDate());
        setEffectiveToDate(currentVob.policyEndDate ? moment(currentVob.policyEndDate).toDate() : moment(`${currentYear}-12-31`).toDate());
    }, [currentVob]);


    useEffect(() => {
        if (currentVob) {
            updateFormDataFromVOB(currentVob);
        }
    }, [networkType, memberType]);

    useEffect(() => {
        const section = location.pathname.split("/")[3] ?? "";
        if (section !== "vob") return; 
        const urlConflictsWithEstimate =
            urlVobId != null &&
            urlVobId !== "" &&
            estimateVobId != null &&
            String(urlVobId) !== String(estimateVobId);

        if (estimateVobId && !urlConflictsWithEstimate) {
            setSelectedVOB(String(estimateVobId));
            const selectedVobNew = vobs.find(v => String(v.vobId) === String(estimateVobId));
            if (selectedVobNew) {
                dispatch(setSelectedVob(selectedVobNew)); // Set in Redux
                setActivePolicy(!!selectedVobNew.activePolicy);
                setCarryover(!!selectedVobNew.isCarryover);
                setLiveVOB(!!selectedVobNew.liveVOB);
                setEffectiveFromDate(selectedVobNew.policyBeginDate ? moment(selectedVobNew.policyBeginDate).toDate() : null);
                setEffectiveToDate(selectedVobNew.policyEndDate ? moment(selectedVobNew.policyEndDate).toDate() : null);
                navigate(`/patients/${patientId}/vob/${estimateVobId}`);
            }
        } else if (urlVobId) {
            setSelectedVOB(urlVobId);
            const selectedVobNew = vobs.find(v => String(v.vobId) === String(urlVobId));
            if (selectedVobNew) {
                dispatch(setSelectedVob(selectedVobNew)); // Set in Redux
                setActivePolicy(!!selectedVobNew.activePolicy);
                setCarryover(!!selectedVobNew.isCarryover);
                setLiveVOB(!!selectedVobNew.liveVOB);
                setEffectiveFromDate(selectedVobNew.policyBeginDate ? moment(selectedVobNew.policyBeginDate).toDate() : null);
                setEffectiveToDate(selectedVobNew.policyEndDate ? moment(selectedVobNew.policyEndDate).toDate() : null);
            }
        }
    }, [urlVobId, vobs, estimateVobId, patientId, navigate, dispatch]);

    useEffect(() => {
        autoFixActivePolicyAttemptedForUrlVobIdRef.current = null;
    }, [urlVobId]);

    const syncedVobRateAsKeyRef = useRef('');
    useEffect(() => {
        let inNetwork: boolean | undefined;
        let syncKey = '';

        const vobRateAs = currentVob?.vobId
            ? (currentVob as Vob & { rateAsPayorPlan?: PayorPlan })?.rateAsPayorPlan
            : undefined;

        if (vobRateAs?.isOverridden && vobRateAs.payor?.inNetwork !== undefined) {
            inNetwork = Boolean(vobRateAs.payor.inNetwork);
            syncKey = `vob:${currentVob!.vobId}:${vobRateAs.plan?.facilityPlanId ?? vobRateAs.plan?.payorPlanId ?? ''}:${inNetwork}`;
        } else {
            const facilityPlanId = rateAsPayorPlan?.plan?.facilityPlanId;
            const rateAsPayorId = rateAsPayorPlan?.payor?.payorId ?? selectedPatientCurrentInsurance?.payorId;
            let facilityPayerInNetwork: boolean | undefined;

            if (facilityPayersState?.length) {
                if (facilityPlanId != null) {
                    const payer = facilityPayersState.find((p: { facilityPayorPlans?: { facilityPayorPlanId: number }[]; inNetwork?: boolean }) =>
                        p.facilityPayorPlans?.some((pl) => Number(pl.facilityPayorPlanId) === Number(facilityPlanId))
                    );
                    if (payer?.inNetwork !== undefined) {
                        facilityPayerInNetwork = Boolean(payer.inNetwork);
                    }
                }
                if (facilityPayerInNetwork === undefined && rateAsPayorId != null) {
                    const payer = facilityPayersState.find(
                        (p: { cfgPayorId?: number; inNetwork?: boolean }) => Number(p.cfgPayorId) === Number(rateAsPayorId)
                    );
                    if (payer?.inNetwork !== undefined) {
                        facilityPayerInNetwork = Boolean(payer.inNetwork);
                    }
                }
            }

            if (facilityPayerInNetwork !== undefined) {
                inNetwork = facilityPayerInNetwork;
                syncKey = `facility:${rateAsPayorId ?? ''}:${facilityPlanId ?? ''}:${inNetwork}`;
            } else if (rateAsPayorPlan?.payor?.inNetwork !== undefined) {
                inNetwork = Boolean(rateAsPayorPlan.payor.inNetwork);
                syncKey = `rateAs:${rateAsPayorPlan.payor.payorId ?? ''}:${rateAsPayorPlan.plan?.facilityPlanId ?? rateAsPayorPlan.plan?.payorPlanId ?? ''}:${inNetwork}`;
            } else {
                return;
            }
        }

        if (syncedVobRateAsKeyRef.current === syncKey) {
            return;
        }
        syncedVobRateAsKeyRef.current = syncKey;
        setNetworkType(inNetwork ? 'in' : 'out');
    }, [
        currentVob?.vobId,
        currentVob?.rateAsPayorPlan,
        rateAsPayorPlan?.payor?.payorId,
        rateAsPayorPlan?.payor?.inNetwork,
        rateAsPayorPlan?.plan?.facilityPlanId,
        rateAsPayorPlan?.plan?.payorPlanId,
        facilityPayersState,
        selectedPatientCurrentInsurance?.payorId,
    ]);

    useEffect(() => {
        if (selectedFacility && payers?.length > 0) {
            const config: any = {
                paramId: API_GET_FILTERED,
                masterListPayers: payers,
                facilityId: selectedFacility,
            };
            dispatch(getEstimatorFacilityPayers(config));
        }
    }, [selectedFacility, payers]);

    const handleFacilityChange = async (facilityId: number) => {
        setSelectedFacility(facilityId);
        dispatch(estimatorSlice.actions.setRateAsPayorPlan(null));
        const selectedClient: Client = {
            clientId: currentVob?.client?.clientId
                ? Number(currentVob.client.clientId)
                : Number(selectedPatient.clientId)
        }

        const selectedFacility: Facility = {
            facilityId: facilityId
        }

        const selectedPayor: Payor = {
            payorId: currentVob?.payor?.payorId
                ?? selectedPatientCurrentInsurance?.payorId
        };

        const selectedPlan: Plan = {
            payorPlanId: currentVob?.plan?.payorPlanId
                ?? selectedPatientCurrentInsurance?.payorPlanId
        };

        // call the rateasPayor w/ new facility
        await loadAndSetRateAsPayorPlan(
            selectedClient,
            selectedFacility,
            dispatch,
            reduxMasterPayors,
            selectedPayor,
            selectedPlan,
            reduxRateAsPayorMap
        );
    };

    const runCRMVob = async () => {
        try {
            if (selectedEncounter.clientsPatientIOCId && selectedEncounter.clientId) {
                setIsRunningCRMVOB(true);
                let clientCrm: any[] = [
                    {
                        crmType: {
                            crmTypeSlug: "salesforce"
                        },
                        preadmitCrmObjectType: {},
                        admitCrmObjectType: {}
                    }
                ];

                const ptRecordRes: any = await dispatch(
                    getPtRecord({
                        leadId: selectedEncounter.clientsPatientIOCId,//,"006VE000006n6SG"
                        clientId: selectedEncounter.clientId,//106134,
                        crm: clientCrm[0]
                    })
                );

                let errorMessage: string = crmInboundErrorMessages.misconfigured;
                if (ptRecordRes.error) {
                    if (
                        ptRecordRes.meta.arg.leadId === undefined ||
                        ptRecordRes.payload === undefined
                    ) {
                        errorMessage = crmInboundErrorMessages.patientError;
                    } else if (Number.isNaN(ptRecordRes.meta.arg.clientId)) {
                        errorMessage = crmInboundErrorMessages.facilityError;
                    }
                    dispatch(showErrorStatus(errorMessage));

                } else if (typeof ptRecordRes.payload === "string") {
                    if (ptRecordRes.payload === "No facility to process.") {
                        errorMessage = crmInboundErrorMessages.facilityError;
                    }
                    dispatch(showErrorStatus(errorMessage));
                }
                const converted = convertCRMVOBToNewVob(ptRecordRes.payload,patientId);

                const isUnique = isVOBUnique(converted);
                if (isUnique) {
                    let newVOBResponse = await dispatch(createSelectedVob(converted));
                    await dispatch(loadPatientVobList(patientId));
                    navigate(`/patients/${patientId}/vob/${newVOBResponse.payload.vobId}`);
                } else {
                    dispatch(showErrorStatus('The insurance verification record already exists and does not need to be added again'));
                }
            }else{
                dispatch(showErrorStatus("selectedEncounter clientsPatientIOCId or clientId is null "));

            }
        } finally {
            setIsRunningCRMVOB(false);
        }
    };

    const getAvailityVOBErrorMessage = (response: any): string => {
        // The availity service always returns { message, errors? } on error.
        // The `message` is already user-friendly (sanitized server-side), so we
        // surface it directly instead of re-translating it here.
        const message = response?.entity?.message;
        if (typeof message === 'string' && message.trim().length > 0) {
            return message;
        }
        return AvailityVOBAErrorMessages.GENERIC_ERROR;
    };

    const runAvailityVob = async () => {
        const missingFields = [];
        const memberId = selectedPatientCurrentInsurance?.memberId || '';

        if (!memberId || memberId.trim() === '') {
            missingFields.push('Member ID');
        }

        if (!selectedPatient?.contact?.firstName || selectedPatient.contact.firstName.trim() === '') {
            missingFields.push('First Name');
        }

        if (!selectedPatient?.contact?.lastName || selectedPatient.contact.lastName.trim() === '') {
            missingFields.push('Last Name');
        }

        if (!selectedPatient?.contact?.birthDate) {
            missingFields.push('Date of Birth');
        }

        if (!selectedPatientCurrentInsurance?.payorId) {
            missingFields.push("Current Insurance Payer");
        }

        if (!selectedPatientCurrentInsurance?.payorPlanId) {
            missingFields.push("Current Insurance Plan");
        }

        if (missingFields.length > 0) {
            const errorMessage = `The following fields are required to run Availity VOB: ${missingFields.join(', ')}`;
            setAvailityErrorMessage(errorMessage);
            setShowAvailityErrorDialog(true);
            return;
        }

        setIsRunningAvailityVOB(true);
        try {
            const responseFromAvaility = await getAvailityVobWithOverrides(
                selectedPatient.patientId,
                selectedFacility as number
            );

            if(responseFromAvaility.hasErrors){
                const errorMessage = getAvailityVOBErrorMessage(responseFromAvaility);
                setAvailityErrorMessage(errorMessage);
                setShowAvailityErrorDialog(true);
                return;
            }else if(responseFromAvaility.entity?.activePolicy === false){
                navigate(`/patients/${patientId}/vob`)
                dispatch(showErrorStatus("Patient's insurance policy is not active"));
            }else{
                const response = await dispatch(createSelectedVob(responseFromAvaility.entity));
                if (response.payload) {
                    await dispatch(loadPatientVobList(patientId));
                    navigate(`/patients/${patientId}/vob/${response.payload.vobId}`);
                }
            }

        } catch (error) {
            console.error('Failed to run Availity VOB:', error);
            dispatch(showErrorStatus("Failed to run Availity VOB"));
        } finally {
            setIsRunningAvailityVOB(false);
        }
    };

    const runAvailityVobWithOverrides = async (
        overrideInsurance?: typeof selectedPatientCurrentInsurance | null,
        overridePatient?: typeof selectedPatient | null
    ) => {
        const missingFields = [];
        const currentInsurance = overrideInsurance || selectedPatientCurrentInsurance;
        const currentPatient = overridePatient || selectedPatient;
        const memberId = currentInsurance?.memberId || '';

        if (!memberId || memberId.trim() === '') {
            missingFields.push('Member ID');
        }

        if (!currentPatient?.contact?.firstName || currentPatient.contact.firstName.trim() === '') {
            missingFields.push('First Name');
        }

        if (!currentPatient?.contact?.lastName || currentPatient.contact.lastName.trim() === '') {
            missingFields.push('Last Name');
        }

        if (!currentPatient?.contact?.birthDate) {
            missingFields.push('Date of Birth');
        }

        if (!currentInsurance?.payorId) {
            missingFields.push("Current Insurance Payer");
        }

        if (!currentInsurance?.payorPlanId) {
            missingFields.push("Current Insurance Plan");
        }

        if (missingFields.length > 0) {
            const errorMessage = `The following fields are required to run Availity VOB: ${missingFields.join(', ')}`;
            setAvailityErrorMessage(errorMessage);
            setShowAvailityErrorDialog(true);
            return;
        }

        setIsRunningAvailityVOB(true);
        try {
            const responseFromAvaility = await getAvailityVobWithOverrides(
                currentPatient.patientId,
                selectedFacility as number
            );

            if(responseFromAvaility.hasErrors){
                const errorMessage = getAvailityVOBErrorMessage(responseFromAvaility);
                setAvailityErrorMessage(errorMessage);
                setShowAvailityErrorDialog(true);
                return;
            }else if(responseFromAvaility.entity?.activePolicy === false){
                navigate(`/patients/${patientId}/vob`)
                dispatch(showErrorStatus("Patient's insurance policy is not active"));
            }else{
                const response = await dispatch(createSelectedVob(responseFromAvaility.entity));
                if (response.payload) {
                    await dispatch(loadPatientVobList(patientId));
                    navigate(`/patients/${patientId}/vob/${response.payload.vobId}`);
                }
            }

        } catch (error) {
            console.error('Failed to run Availity VOB:', error);
            dispatch(showErrorStatus("Failed to run Availity VOB"));
        } finally {
            setIsRunningAvailityVOB(false);
        }
    };

    const handleInputChange = (field: keyof VOBFormData, value: string | boolean) => {
        setFormData(prev => {
            const newFormData = {
                ...prev,
                [field]: value
            };

            const combinationKey = `${networkType}_${memberType}`;
            setSavedFormDataMap(prevMap => {
                const newMap = new Map(prevMap);
                newMap.set(combinationKey, newFormData);
                return newMap;
            });

            return newFormData;
        });
    };

    const handleVOBSelectChange = (vobId: string) => {
        dispatch(clearSelectedEstimate());
        setSelectedVOB(vobId);
        if (vobId) {
            const selectedVobNew = vobs.find(v => String(v.vobId) === String(vobId));
            if (selectedVobNew) {
                dispatch(setSelectedVob(selectedVobNew));
                updateFormDataFromVOB(selectedVobNew);
                setActivePolicy(!!selectedVobNew.activePolicy);
                setCarryover(!!selectedVobNew.isCarryover);
                setLiveVOB(!!selectedVobNew.liveVOB);

                // Navigate after state updates to ensure consistent state
                navigate(`/patients/${patientId}/vob/${vobId}`);
            }
        } else {
            // Handle empty selection
            dispatch(setSelectedVob(null)); // Clear Redux state
            navigate(`/patients/${patientId}/vob`);
        }
    };



    const saveAndRestoreFormData = (
        oldNetworkType: NetworkType,
        oldMemberType: MemberType,
        newNetworkType: NetworkType,
        newMemberType: MemberType
    ) => {
        const savedFormData = {
            deductible: formData.deductible,
            remaining: formData.remaining,
            oopm: formData.oopm,
            oopmRemaining: formData.oopmRemaining,
            copay: formData.copay,
            maxDays: formData.maxDays,
            coinsurance: formData.coinsurance,
            notes: formData.notes,
            isDedIncOOPM: formData.isDedIncOOPM
        };
        const combinationKey = `${oldNetworkType}_${oldMemberType}`;
        setSavedFormDataMap(prevMap => {
            const newMap = new Map(prevMap);
            newMap.set(combinationKey, savedFormData);
            return newMap;
        });

        const currentCombinationKey = `${newNetworkType}_${newMemberType}`;
        const restoredFormData = savedFormDataMap.get(currentCombinationKey);
        if (restoredFormData) {
            setFormData(restoredFormData);
        } else {
            setFormData({
                deductible: '',
                remaining: '',
                oopm: '',
                oopmRemaining: '',
                copay: '',
                maxDays: '',
                coinsurance: '',
                notes: '',
                isDedIncOOPM: true,
            });
            if (currentVob) {
                updateFormDataFromVOB(currentVob);
            }
        }

    };

    const handleNetworkTypeChange = (type: NetworkType) => {
        if (type !== networkType) {
            saveAndRestoreFormData(networkType, memberType, type, memberType);
            setNetworkType(type);
            onTabChange?.();
        }
    };

    const handleMemberTypeChange = (type: MemberType) => {
        if (type !== memberType) {
            setMemberType(type);
            saveAndRestoreFormData(networkType, memberType, networkType, type);
            setMemberType(type);
            onTabChange?.();
        }
    };

    const handleEffectiveFromDateChange = (date: Date | null) => {
        setEffectiveFromDate(date);
    };

    const handleEffectiveToDateChange = (date: Date | null) => {
        setEffectiveToDate(date);
    };

    const updateFormDataFromVOB = (vob: Vob) => {
        const restoredFormData = savedFormDataMap.get(networkType+"_"+memberType);
        let deductible = String(vob.innDeductible?.total ?? '');
        let remaining = String(vob.innDeductible?.remaining ?? '');
        let oopm = String(vob.innOopm?.total ?? '');
        let oopmRemaining = String(vob.innOopm?.remaining ?? '');
        let isDedIncOOPM = false;
        let copay = '';
        let maxDays = '';
        let coInsurance =  '';
        if(restoredFormData){
            deductible = restoredFormData.deductible;
            remaining = restoredFormData.remaining;
            oopm = restoredFormData.oopm;
            oopmRemaining = restoredFormData.oopmRemaining;
            isDedIncOOPM = restoredFormData.isDedIncOOPM;
            copay = restoredFormData.copay;
            maxDays = restoredFormData.maxDays;
            coInsurance =  restoredFormData.coinsurance;
        }else {
            if (networkType === 'in' && memberType === 'individual') {
                deductible = String(vob.innDeductible?.total ?? '');
                remaining = String(vob.innDeductible?.remaining ?? '');
                oopm = String(vob.innOopm?.total ?? '');
                oopmRemaining = String(vob.innOopm?.remaining ?? '');
                isDedIncOOPM = vob.innOopm?.isDeductibleIncluded ?? false;
                copay = String(vob.innCoPay ?? '');
                maxDays = String(vob.innMaxDays ?? '');
                coInsurance = String(vob.innCoInsurance ?? '');
            } else if (networkType === 'in' && memberType === 'family') {
                deductible = String(vob.innFamilyDeductible?.total ?? '');
                remaining = String(vob.innFamilyDeductible?.remaining ?? '');
                oopm = String(vob.innFamilyOopm?.total ?? '');
                oopmRemaining = String(vob.innFamilyOopm?.remaining ?? '');
                isDedIncOOPM = vob.innFamilyOopm?.isDeductibleIncluded ?? false;
                copay = String(vob.innFamilyCoPay ?? '');
                maxDays = String(vob.innFamilyMaxDays ?? '');
                coInsurance = String(vob.innFamilyCoInsurance ?? '');
            } else if (networkType === 'out' && memberType === 'individual') {
                deductible = String(vob.oonDeductible?.total ?? '');
                remaining = String(vob.oonDeductible?.remaining ?? '');
                oopm = String(vob.oonOopm?.total ?? '');
                oopmRemaining = String(vob.oonOopm?.remaining ?? '');
                isDedIncOOPM = vob.oonOopm?.isDeductibleIncluded ?? false;
                copay = String(vob.oonCoPay ?? '');
                maxDays = String(vob.oonMaxDays ?? '');
                coInsurance = String(vob.oonCoInsurance ?? '');
            } else if (networkType === 'out' && memberType === 'family') {
                deductible = String(vob.oonFamilyDeductible?.total ?? '');
                remaining = String(vob.oonFamilyDeductible?.remaining ?? '');
                oopm = String(vob.oonFamilyOopm?.total ?? '');
                oopmRemaining = String(vob.oonFamilyOopm?.remaining ?? '');
                isDedIncOOPM = vob.oonFamilyOopm?.isDeductibleIncluded ?? false;
                copay = String(vob.oonFamilyCoPay ?? '');
                maxDays = String(vob.oonFamilyMaxDays ?? '');
                coInsurance = String(vob.oonFamilyCoInsurance ?? '');
            }
        }

        const notes = vob.notes && vob.notes.length > 0 ? vob.notes[0].noteText : '';

        setFormData({
            deductible: deductible,
            remaining: remaining,
            oopm: oopm,
            oopmRemaining: oopmRemaining,
            copay: copay,
            maxDays: maxDays,
            coinsurance: coInsurance,
            notes: notes,
            isDedIncOOPM: isDedIncOOPM,
        });

        if (onSetButtonStates) {
            const buttonStates = {
                isDeductibleButtonPressed: !deductible ,
                isOopmButtonPressed: !oopm  ,
                isServiceLimitsButtonPressed: !copay,
                isMaxDaysButtonPressed: !maxDays,
                isCoinsuranceButtonPressed: !coInsurance,
            };
            onSetButtonStates(buttonStates);
        }
    };
    const createBaseVobClassification = (classification: any, copayPerDayVisit: string): VobClassification => ({
        vobClassificationId: classification.vobClassificationId,
        copayType: copayPerDayVisit && copayPerDayVisit.trim() !== '' ? "Yes" : "No",
        isInpatient: !!classification.isInpatient,
        notes: []
    } as VobClassification);

    const applyValuesToVobClassification = (
        vobClassification: VobClassification,
        values: {
            total?: string;
            remaining?: string;
            oopmTotal?: string;
            oopmRemaining?: string;
            deductibleIncluded?: boolean;
            perDayVisit?: string;
            maxDays?: string;
            patient?: string;
        },
        memberType: MemberType
    ) => {
        const isIndividual = memberType === 'individual';
        if (values.total && values.total.trim() !== '') {
            const deductible = {
                total: Number(values.total),
                met: Number(values.total) - Number(values.remaining),
                remaining: Number(values.remaining),
            };

            if (isIndividual) {
                vobClassification.deductible = deductible;
            } else {
                vobClassification.familyDeductible = deductible;
            }
        }else{
            if (isIndividual) {
                delete vobClassification.deductible;
            } else {
                delete vobClassification.familyDeductible;
            }
        }

        if (values.oopmTotal && values.oopmTotal.trim() !== '') {
            const oopm = {
                total: Number(values.oopmTotal),
                met: Number(values.oopmTotal) - Number(values.oopmRemaining),
                remaining: Number(values.oopmRemaining),
                isDeductibleIncluded: values.deductibleIncluded ?? true,
            };
            if (isIndividual) {
                vobClassification.oopm = oopm;
            } else {
                vobClassification.familyOopm = oopm;
            }
        }else{
            if (isIndividual) {
                delete vobClassification.oopm;
            } else {
                delete vobClassification.familyOopm;
            }
        }

        if (values.perDayVisit && values.perDayVisit.trim() !== '') {
            if (isIndividual) {
                vobClassification.coPay = Number(values.perDayVisit);
            } else {
                vobClassification.familyCoPay = Number(values.perDayVisit);
            }
        }else{
            if (isIndividual) {
                delete vobClassification.coPay;
            } else {
                delete vobClassification.familyCoPay;
            }
        }

        if (values.maxDays && values.maxDays.trim() !== '') {
            if (isIndividual) {
                vobClassification.maxDays = Number(values.maxDays);
            } else {
                vobClassification.familyMaxDays = Number(values.maxDays);
            }
        }else{
            if (isIndividual) {
                delete vobClassification.maxDays;
            } else {
                delete vobClassification.familyMaxDays;
            }
        }

        if (values.patient && values.patient.trim() !== '') {
            if (isIndividual) {
                vobClassification.coInsurance = Number(values.patient);
            } else {
                vobClassification.familyCoInsurance = Number(values.patient);
            }
        }else{
            if (isIndividual) {
                delete vobClassification.coInsurance;
            } else {
                delete vobClassification.familyCoInsurance;
            }
        }
    };

    const buildVobClassifications = (): {innClassifications: VobClassification[], oonClassifications: VobClassification[]} => {
        if (!vobClassifications || vobClassifications.length === 0) {
            throw new Error("VOB classifications not loaded. Please ensure classifications are loaded before updating VOB.");
        }
        let innClassifications: VobClassification[] = [];
        let oonClassifications: VobClassification[] = [];
        const keysArray = Array.from(savedLOCDataMap.keys());

        const buildNotesFromText = (text: unknown): VobClassification['notes'] => {
            if (typeof text !== 'string') return [];
            return text
                .split('\n')
                .map(line => line.trim())
                .filter(Boolean)
                .map(noteText => ({ noteText }));
        };

        for (let i = 0; i < keysArray.length; i++) {
            const locData = savedLOCDataMap.get(keysArray[i]);
            const inIndividualData = locData.get('in_individual');
            const inFamilyData = locData.get('in_family');
            const oonIndividualData = locData.get('out_individual');
            const oonFamilyData = locData.get('out_family');
            const inNotesText = locData.get('in_notes');
            const oonNotesText = locData.get('out_notes');

            const classification = vobClassifications.find(c => c.vobClassificationId === keysArray[i]);
            if (!classification) {
                continue;
            }

            const inVobClassification = createBaseVobClassification(classification, inIndividualData?.perDayVisit || '');
            inVobClassification.notes = buildNotesFromText(inNotesText);
            let inPush = false;
            if (inIndividualData){
                applyValuesToVobClassification(inVobClassification, inIndividualData, "individual");
                inPush= true;
            }
            if (inFamilyData){
                applyValuesToVobClassification(inVobClassification, inFamilyData, "family");
                inPush = true;
            }
            if(inPush) {
                innClassifications.push(inVobClassification);
            }

            const oonVobClassification = createBaseVobClassification(classification, inIndividualData?.perDayVisit || '');
            oonVobClassification.notes = buildNotesFromText(oonNotesText);
            let oonPush = false;
            if (oonIndividualData){
                applyValuesToVobClassification(oonVobClassification, oonIndividualData, "individual");
                oonPush = true;
            }

            if (oonFamilyData){
                applyValuesToVobClassification(oonVobClassification, oonFamilyData, "family");
                oonPush = true;
            }
            if(oonPush) {
                oonClassifications.push(oonVobClassification);
            }
        }
        return { innClassifications, oonClassifications };
    };

    const applyFormDataToVob = (vob: Vob, shouldDeleteEmptyFields: boolean = false) => {
        const keysArray = Array.from(savedFormDataMap.keys());

        for (let i = 0; i < keysArray.length; i++) {
            const key = keysArray[i];
            const parts = key.split("_");
            const networkTypeMap = parts[0];
            const memberTypeMap = parts[1];
            const restoredFormData = savedFormDataMap.get(key);

            if (networkTypeMap === 'in' && memberTypeMap === 'individual') {
                if (restoredFormData.copay && restoredFormData.copay.trim()) {
                    vob.innCoPay = Number(restoredFormData.copay);
                } else if (shouldDeleteEmptyFields) {
                    delete vob.innCoPay;
                }
                if (restoredFormData.maxDays && restoredFormData.maxDays.trim()) {
                    vob.innMaxDays = Number(restoredFormData.maxDays);
                } else if (shouldDeleteEmptyFields) {
                    delete vob.innMaxDays;
                }
                if (restoredFormData.coinsurance && restoredFormData.coinsurance.trim()) {
                    vob.innCoInsurance = Number(restoredFormData.coinsurance);
                } else if (shouldDeleteEmptyFields) {
                    delete vob.innCoInsurance;
                }
            } else if (networkTypeMap === 'in' && memberTypeMap === 'family') {
                if (restoredFormData.copay && restoredFormData.copay.trim()) {
                    vob.innFamilyCoPay = Number(restoredFormData.copay);
                } else if (shouldDeleteEmptyFields) {
                    delete vob.innFamilyCoPay;
                }
                if (restoredFormData.maxDays && restoredFormData.maxDays.trim()) {
                    vob.innFamilyMaxDays = Number(restoredFormData.maxDays);
                } else if (shouldDeleteEmptyFields) {
                    delete vob.innFamilyMaxDays;
                }
                if (restoredFormData.coinsurance && restoredFormData.coinsurance.trim()) {
                    vob.innFamilyCoInsurance = Number(restoredFormData.coinsurance);
                } else if (shouldDeleteEmptyFields) {
                    delete vob.innFamilyCoInsurance;
                }
            } else if (networkTypeMap === 'out' && memberTypeMap === 'individual') {
                if (restoredFormData.copay && restoredFormData.copay.trim()) {
                    vob.oonCoPay = Number(restoredFormData.copay);
                } else if (shouldDeleteEmptyFields) {
                    delete vob.oonCoPay;
                }
                if (restoredFormData.maxDays && restoredFormData.maxDays.trim()) {
                    vob.oonMaxDays = Number(restoredFormData.maxDays);
                } else if (shouldDeleteEmptyFields) {
                    delete vob.oonMaxDays;
                }
                if (restoredFormData.coinsurance && restoredFormData.coinsurance.trim()) {
                    vob.oonCoInsurance = Number(restoredFormData.coinsurance);
                } else if (shouldDeleteEmptyFields) {
                    delete vob.oonCoInsurance;
                }
            } else if (networkTypeMap === 'out' && memberTypeMap === 'family') {
                if (restoredFormData.copay && restoredFormData.copay.trim()) {
                    vob.oonFamilyCoPay = Number(restoredFormData.copay);
                } else if (shouldDeleteEmptyFields) {
                    delete vob.oonFamilyCoPay;
                }
                if (restoredFormData.maxDays && restoredFormData.maxDays.trim()) {
                    vob.oonFamilyMaxDays = Number(restoredFormData.maxDays);
                } else if (shouldDeleteEmptyFields) {
                    delete vob.oonFamilyMaxDays;
                }
                if (restoredFormData.coinsurance && restoredFormData.coinsurance.trim()) {
                    vob.oonFamilyCoInsurance = Number(restoredFormData.coinsurance);
                } else if (shouldDeleteEmptyFields) {
                    delete vob.oonFamilyCoInsurance;
                }
            }
        }

        if (formData.notes && formData.notes.trim()) {
            vob.notes = [{
                noteText: formData.notes,
                noteDt: new Date().toISOString()
            }];
        }

        for (let i = 0; i < keysArray.length; i++) {
            const key = keysArray[i];
            const parts = key.split("_");
            const networkTypeMap = parts[0];
            const memberTypeMap = parts[1];
            const restoredFormData = savedFormDataMap.get(key);

            if (networkTypeMap === 'in' && memberTypeMap === 'individual') {
                const deductibleData = {
                    total: Number(restoredFormData.deductible),
                    met: Number(restoredFormData.deductible-restoredFormData.remaining),
                    remaining: Number(restoredFormData.remaining),
                }
                const oopmData = {
                    total:  Number(restoredFormData.oopm),
                    met: Number(restoredFormData.oopm) - Number(restoredFormData.oopmRemaining),
                    remaining: Number(restoredFormData.oopmRemaining),
                    isDeductibleIncluded: restoredFormData.isDedIncOOPM,
                }

                if (restoredFormData.deductible !=='' && restoredFormData.remaining !=='') {
                    vob.innDeductible = deductibleData;
                } else if (shouldDeleteEmptyFields) {
                    delete vob.innDeductible;
                }
                if (restoredFormData.oopm !=='' && restoredFormData.oopmRemaining !=='') {
                    vob.innOopm = oopmData;
                } else if (shouldDeleteEmptyFields) {
                    delete vob.innOopm;
                }
            } else if (networkTypeMap === 'in' && memberTypeMap === 'family') {
                const deductibleData = {
                    total: Number(restoredFormData.deductible),
                    met: Number(restoredFormData.deductible-restoredFormData.remaining),
                    remaining: Number(restoredFormData.remaining),
                }
                const oopmData = {
                    total:  Number(restoredFormData.oopm),
                    met: Number(restoredFormData.oopm) - Number(restoredFormData.oopmRemaining),
                    remaining: Number(restoredFormData.oopmRemaining),
                    isDeductibleIncluded: restoredFormData.isDedIncOOPM,
                }

                if (restoredFormData.deductible !=='' && restoredFormData.remaining !=='') {
                    vob.innFamilyDeductible = deductibleData;
                } else if (shouldDeleteEmptyFields) {
                    delete vob.innFamilyDeductible;
                }
                if (restoredFormData.oopm !=='' && restoredFormData.oopmRemaining !=='') {
                    vob.innFamilyOopm = oopmData;
                } else if (shouldDeleteEmptyFields) {
                    delete vob.innFamilyOopm;
                }
            } else if (networkTypeMap === 'out' && memberTypeMap === 'individual') {
                const deductibleData = {
                    total: Number(restoredFormData.deductible),
                    met: Number(restoredFormData.deductible-restoredFormData.remaining),
                    remaining: Number(restoredFormData.remaining),
                }
                const oopmData = {
                    total:  Number(restoredFormData.oopm),
                    met: Number(restoredFormData.oopm) - Number(restoredFormData.oopmRemaining),
                    remaining: Number(restoredFormData.oopmRemaining),
                    isDeductibleIncluded: restoredFormData.isDedIncOOPM,
                }

                if (restoredFormData.deductible !=='' && restoredFormData.remaining !=='') {
                    vob.oonDeductible = deductibleData;
                } else if (shouldDeleteEmptyFields) {
                    delete vob.oonDeductible;
                }
                if (restoredFormData.oopm !=='' && restoredFormData.oopmRemaining !=='') {
                    vob.oonOopm = oopmData;
                } else if (shouldDeleteEmptyFields) {
                    delete vob.oonOopm;
                }
            } else if (networkTypeMap === 'out' && memberTypeMap === 'family') {
                const deductibleData = {
                    total: Number(restoredFormData.deductible),
                    met: Number(restoredFormData.deductible-restoredFormData.remaining),
                    remaining: Number(restoredFormData.remaining),
                }
                const oopmData = {
                    total:  Number(restoredFormData.oopm),
                    met: Number(restoredFormData.oopm) - Number(restoredFormData.oopmRemaining),
                    remaining: Number(restoredFormData.oopmRemaining),
                    isDeductibleIncluded: restoredFormData.isDedIncOOPM,
                }

                if (restoredFormData.deductible !=='' && restoredFormData.remaining !=='') {
                    vob.oonFamilyDeductible = deductibleData;
                } else if (shouldDeleteEmptyFields) {
                    delete vob.oonFamilyDeductible;
                }
                if (restoredFormData.oopm !=='' && restoredFormData.oopmRemaining !=='') {
                    vob.oonFamilyOopm = oopmData;
                } else if (shouldDeleteEmptyFields) {
                    delete vob.oonFamilyOopm;
                }
            }
        }

        const classifications = buildVobClassifications();
        vob.innClassifications = classifications.innClassifications;
        vob.oonClassifications = classifications.oonClassifications;
    };

    const buildUpdatedVob = (overrides?: { activePolicy?: boolean }): Vob => {

        if (!currentVob) throw new Error('Current VOB is required');

        const { vobId, ...vobWithoutId } = currentVob;
        const updatedVob: Vob = {
            ...vobWithoutId,
            activePolicy: overrides?.activePolicy !== undefined ? overrides.activePolicy : activePolicy,
            isCarryover: carryover,
            liveVOB,
            policyBeginDate: effectiveFromDate?.toISOString().split('T')[0] || currentVob.policyBeginDate,
            policyEndDate: effectiveToDate?.toISOString().split('T')[0] || currentVob.policyEndDate,
            facility: {
                facilityId: selectedFacility || currentVob.facility?.facilityId,
            },
        };

        // Fix for validation errors:
        // Ensure groupNum and policyNum are not empty strings
        if (updatedVob.groupNum && updatedVob.groupNum.trim() === '') {
            delete updatedVob.groupNum;
        }

        if (updatedVob.policyNum && updatedVob.policyNum.trim() === '') {
            delete updatedVob.policyNum;
        }

        applyFormDataToVob(updatedVob, true);

        const rateAsPayorPlanToSave = getRateAsPayorPlanToSave?.(facilityPayersState, payersMap, rateAsPayorPlan);
        if (rateAsPayorPlanToSave != null) {
            // Backend schema requires rateAsPayorPlan.payorPlanId (and payorId) at root level
            updatedVob.rateAsPayorPlan = {
                ...rateAsPayorPlanToSave,
                payorPlanId: rateAsPayorPlanToSave.plan?.payorPlanId,
                payorId: rateAsPayorPlanToSave.payor?.payorId,
            } as any;
        }
        return updatedVob;
    };

    const buildNewVob = (): Vob => {
        const currentYear = new Date().getFullYear();
        const payorData: any = {
            "payorId": selectedPatientCurrentInsurance.payorId,
            "payorPlanId": selectedPatientCurrentInsurance.payorPlanId,
        };

        if (rateAsPayorPlan?.payor?.facilityPayorName || rateAsPayorPlan?.payor?.payorName) {
            payorData.payorName = rateAsPayorPlan.payor.facilityPayorName || rateAsPayorPlan.payor.payorName;
            payorData.facilityPayorName = rateAsPayorPlan.payor.facilityPayorName || rateAsPayorPlan.payor.payorName;
        }

        const planData: any = {
            "payorPlanId": selectedPatientCurrentInsurance.payorPlanId,
            "facilityPlanId": rateAsPayorPlan?.plan?.facilityPlanId,
        };

        if (rateAsPayorPlan?.plan?.facilityPlanName || rateAsPayorPlan?.plan?.planName) {
            planData.planName = rateAsPayorPlan.plan.facilityPlanName || rateAsPayorPlan.plan.planName;
            planData.facilityPlanName = rateAsPayorPlan.plan.facilityPlanName || rateAsPayorPlan.plan.planName;
        }

        const newVob: Vob = {
            "patientId": selectedPatient.patientId,
            "client": {
                "clientId":  Number(selectedPatient.clientId),
            },
            "facility": {
                "facilityId": selectedFacility,
            },
            "payor": payorData,
            "plan": planData,
            "vobSourceId": 4, // manual
            "isValid": false,
            "digitalVerificationMethod": false,
            liveVOB,
            activePolicy,
            isCarryover: carryover,
            "isCopayReset": false,
            "policyBeginDate": effectiveFromDate?.toISOString().split('T')[0] || `${currentYear}-01-01`,
            "policyEndDate": effectiveToDate?.toISOString().split('T')[0] || `${currentYear}-12-31`,
        } as Vob;

        // Only add groupNum if it's not empty after trimming
        if (selectedPatientCurrentInsurance.groupNumber && selectedPatientCurrentInsurance.groupNumber.trim() !== '') {
            newVob.groupNum = selectedPatientCurrentInsurance.groupNumber;
        }

        // Only add policyNum if it's not empty after trimming
        if (selectedPatientCurrentInsurance.memberId && selectedPatientCurrentInsurance.memberId.trim() !== '') {
            newVob.policyNum = selectedPatientCurrentInsurance.memberId;
        }

        applyFormDataToVob(newVob, false);

        const rateAsPayorPlanToSave = getRateAsPayorPlanToSave?.(facilityPayersState, payersMap, rateAsPayorPlan);
        if (rateAsPayorPlanToSave != null) {
            // Backend schema requires rateAsPayorPlan.payorPlanId (and payorId) at root level.
            newVob.rateAsPayorPlan = {
                ...rateAsPayorPlanToSave,
                payorPlanId: rateAsPayorPlanToSave.plan?.payorPlanId,
                payorId: rateAsPayorPlanToSave.payor?.payorId,
                isOverridden: true,
            } as any;
        }
        return newVob;
    };

    const syncVobToCrmAfterSave = async (newVobData: Vob) => {
        const encounterId =
            selectedEstimate?.patientEncounterId ??
            selectedEncounter?.patientEncounterId;
        if (!newVobData?.client?.clientId || !encounterId) {
            return;
        }
        const clientCrm = getClientCrmDetails(
            newVobData.client.clientId,
            allClients ?? []
        );
        if (!checkIntegrationFeatureFlag(clientCrm)) {
            return;
        }
        if (
            !clientCrm[0]?.isVobUpdateEnabled &&
            !clientCrm[0]?.isBenefitsUpdateEnabled
        ) {
            return;
        }
        if (!selectedEncounter) {
            return;
        }
        if (
            Number(selectedEncounter.patientEncounterId) !== Number(encounterId)
        ) {
            return;
        }

        const formedClient = { ...newVobData.client };
        const formedFacility = { ...newVobData.facility };
        delete (formedClient as { clientList?: unknown }).clientList;
        delete (formedFacility as { facilityList?: unknown }).facilityList;

        const getVOBPayload = buildCrmVobUpdatePayload(
            newVobData,
            selectedEncounter.clientsPatientAccountId,
            selectedEncounter.clientsPatientIOCId,
            formedClient,
            formedFacility,
            {
                firstName: selectedPatient.contact?.firstName ?? "",
                lastName: selectedPatient.contact?.lastName ?? "",
                birthDate: selectedPatient.contact?.birthDate ?? null,
            }
        );

        await integrationVOBUpdate({
            vob: getVOBPayload,
            crmTypeSlug: clientCrm[0].crmType.crmTypeSlug,
        });
    };

    const handleSaveVOB = async (): Promise<Vob | Boolean> => {
        try {
            let savedVobId: number | undefined;
            let savedVob: Vob | undefined;

            if (selectedVOB && currentVob) {
                const updatedVob = buildUpdatedVob();
                setSavedFormDataMap(new Map());
                setSavedLOCDataMap(new Map());

                if (updatedVob.groupNum?.trim() === '')
                    delete updatedVob.groupNum;
                if (updatedVob.policyNum?.trim() === '')
                    delete updatedVob.policyNum;

                const {payload} = await dispatch(
                    saveSelectedVob({vobId: currentVob.vobId!, vob: updatedVob})
                );

                if (!payload) return false;
                savedVobId = payload.vobId;
                savedVob = payload;
            } else {
                // CREATE
                const newVob = buildNewVob();
                setSavedFormDataMap(new Map());
                setSavedLOCDataMap(new Map());

                if (newVob.groupNum?.trim() === '') delete newVob.groupNum;
                if (newVob.policyNum?.trim() === '') delete newVob.policyNum;

                if (isVobBusinessDuplicate(newVob)) {
                    dispatch(
                        showErrorStatus(
                            'A VOB with the same configuration and date already exists. Please use a different configuration.'
                        )
                    );
                    return false;
                }

                const response =
                    await admissionsAdvisorService.newVobV3(newVob);
                if (!response.entity) return false;
                savedVobId = response.entity.vobId;
                savedVob = response.entity;
            }

            if (savedVob) {
                try {
                    await syncVobToCrmAfterSave(savedVob);
                } catch (crmError) {
                    console.error("CRM VOB sync failed:", crmError);
                }
            }

            // Shared success path for BOTH branches
            await dispatch(loadPatientVobList(patientId));
            navigate(`/patients/${patientId}/vob/${savedVobId}`);

            return savedVob!;
        } catch (error) {
            console.error('Error saving VOB:', error);
            return false;
        }
    };

    useEffect(() => {
        const section = location.pathname.split("/")[3] ?? "";
        if (section !== "vob" || !urlVobId || !currentVob || !selectedVOB) return;
        if (String(currentVob.vobId) !== String(urlVobId)) return;
        if (facilitiesLoading) return;
        const facilityReady =
            selectedFacility !== "" || currentVob.facility?.facilityId != null;
        if (!facilityReady) return;

        const effectiveTo = currentVob.policyEndDate
            ? moment(currentVob.policyEndDate).toDate()
            : null;
        if (!isActivePolicyEffectiveToBeforeToday(!!currentVob.activePolicy, effectiveTo)) return;
        if (autoFixActivePolicyAttemptedForUrlVobIdRef.current === urlVobId) return;

        void (async () => {
            if (autoFixActivePolicyAttemptedForUrlVobIdRef.current === urlVobId) return;
            try {
                const updatedVob = buildUpdatedVob({ activePolicy: false });
                if (currentVob.policyBeginDate) {
                    updatedVob.policyBeginDate = currentVob.policyBeginDate;
                }
                if (currentVob.policyEndDate) {
                    updatedVob.policyEndDate = currentVob.policyEndDate;
                }
                if (updatedVob.groupNum?.trim() === "") delete updatedVob.groupNum;
                if (updatedVob.policyNum?.trim() === "") delete updatedVob.policyNum;
                autoFixActivePolicyAttemptedForUrlVobIdRef.current = urlVobId;
                await dispatch(
                    saveSelectedVob({ vobId: currentVob.vobId!, vob: updatedVob })
                ).unwrap();
                setActivePolicy(false);
                await dispatch(loadPatientVobList(patientId));
                navigate(`/patients/${patientId}/vob/${String(currentVob.vobId)}`);
            } catch (e) {
                console.error("Auto-save Active Policy fix failed:", e);
                autoFixActivePolicyAttemptedForUrlVobIdRef.current = null;
                dispatch(
                    showErrorStatus(
                        "Could not save after turning off Active Policy automatically. Please save manually."
                    )
                );
            }
        })();
    }, [
        location.pathname,
        urlVobId,
        currentVob,
        selectedVOB,
        facilitiesLoading,
        selectedFacility,
        dispatch,
        patientId,
        navigate,
    ]);

    return {
        // States
        activePolicy,
        carryover,
        selectedVOB,
        memberId:
            currentVob?.subscriber?.memberId?.trim() ||
            selectedPatientCurrentInsurance?.memberId?.trim() ||
            '',
        facilities,
        selectedFacility,
        facilitiesLoading,
        networkType,
        memberType,
        liveVOB,
        levelsOfCare,
        levelsLoading,
        levelsError,
        formData,
        currentVob,
        isRunningCRMVOB,
        isRunningAvailityVOB,
        isLoadingRunAvailityVOBButton,
        effectiveFromDate,
        effectiveToDate,
        facilityPayersState,
        rateAsPayorPlan,
        isPayerMatched,
        comparisonError,
        selectedPatient,
        selectedPatientCurrentInsurance,
        payersMap,
        estimateId,
        savedLOCDataMap,
        savedFormDataMap,
        showAvailityErrorDialog,
        availityErrorMessage,

        // Handlers
        handleInputChange,
        handleVOBSelectChange,
        handleNetworkTypeChange,
        handleMemberTypeChange,
        handleEffectiveFromDateChange,
        handleEffectiveToDateChange,
        handleFacilityChange,
        setSelectedFacility,
        setActivePolicy,
        setCarryover,
        setLiveVOB,
        runCRMVob,
        runAvailityVob,
        runAvailityVobWithOverrides,
        handleSaveVOB,
        setSavedFormDataMap,
        setSavedLOCDataMap,
        setShowAvailityErrorDialog,
    };
}